import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { apiClient as api } from '../services/apiClient';

export const fetchDashboardSummary = createAsyncThunk(
  'predictive/fetchDashboardSummary',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/predictive/summary');
      return response.data.summary;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch predictive summary');
    }
  }
);

export const fetchDenialRiskPredictions = createAsyncThunk(
  'predictive/fetchDenialRiskPredictions',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/predictive/denials');
      return response.data.predictions;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch denial predictions');
    }
  }
);

export const fetchAdherenceRiskPredictions = createAsyncThunk(
  'predictive/fetchAdherenceRiskPredictions',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/predictive/adherence');
      return response.data.predictions;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch adherence predictions');
    }
  }
);

export const fetchInventoryForecasts = createAsyncThunk(
  'predictive/fetchInventoryForecasts',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/predictive/inventory');
      return response.data.forecasts;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch inventory forecasts');
    }
  }
);

export const fetchPipExhaustion = createAsyncThunk(
  'predictive/fetchPipExhaustion',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/predictive/pip-exhaustion');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch PIP exhaustion forecasts');
    }
  }
);

export const fetchAppealPredictions = createAsyncThunk(
  'predictive/fetchAppealPredictions',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/predictive/appeal-success');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch appeal success predictions');
    }
  }
);

export const fetchDenialRootCauseAnalysis = createAsyncThunk(
  'predictive/fetchDenialRootCauseAnalysis',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/predictive/denial-root-causes');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch denial root cause analysis');
    }
  }
);

export const predictCaseProfitability = createAsyncThunk(
  'predictive/predictCaseProfitability',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await api.post('/predictive/case-profitability', payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to predict case profitability');
    }
  }
);

export const fetchProfitabilityDashboard = createAsyncThunk(
  'predictive/fetchProfitabilityDashboard',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/predictive/case-profitability/dashboard');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch case profitability dashboard');
    }
  }
);

export const fetchPriorAuthPredictions = createAsyncThunk(
  'predictive/fetchPriorAuthPredictions',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/predictive/prior-auth');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch prior authorization predictions');
    }
  }
);

export const checkPriorAuthPrediction = createAsyncThunk(
  'predictive/checkPriorAuthPrediction',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await api.post('/predictive/prior-auth/check', payload);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to check prior authorization prediction');
    }
  }
);

export const fetchMeddTrajectories = createAsyncThunk(
  'predictive/fetchMeddTrajectories',
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get('/predictive/medd-trajectories');
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch MEDD trajectories');
    }
  }
);

export const simulateMeddDispense = createAsyncThunk(
  'predictive/simulateMeddDispense',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await api.post('/predictive/medd-simulate', payload);
      return {
        ...response.data,
        patientId: payload.patientId,
        simulationKey: `${payload.patientId}:${payload.ndcCode || payload.drugName || 'script'}`
      };
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || 'Failed to simulate MEDD dispense');
    }
  }
);

const initialState = {
  summary: {
    totalDenialRiskAmount: 0,
    totalAdherenceRevenueAtRisk: 0,
    criticalInventoryItems: 0,
  },
  denialPredictions: [],
  adherencePredictions: [],
  inventoryForecasts: [],
  pipExhaustion: {
    forecasts: [],
    summary: {
      totalPatientsAtRisk: 0,
      criticalCount: 0,
      warningCount: 0,
      nextFillExhaustCount: 0,
      totalRevenueExposure: 0,
    },
    loading: false,
    error: null,
  },
  appealPredictions: {
    predictions: [],
    summary: {
      totalDeniedClaims: 0,
      totalDeniedAmount: 0,
      worthPursuingCount: 0,
      totalRecoverableRevenue: 0,
      avgSuccessProbability: 0,
    },
    loading: false,
    error: null,
  },
  denialRootCauses: {
    rootCauses: [],
    summary: {
      totalDenialCount: 0,
      totalDeniedAmount: 0,
      topRootCauses: [],
      fixableRootCauseCount: 0,
      totalRecoverableIfFixed: 0,
    },
    loading: false,
    error: null,
  },
  caseProfitability: {
    dashboard: {
      totalNewCases: 0,
      priorityCases: 0,
      standardCases: 0,
      cautionCases: 0,
      projectedMonthlyRevenue: 0,
      projectedMonthlyProfit: 0,
      byType: [],
      byReferralSource: [],
    },
    prediction: null,
    loading: false,
    error: null,
    predictionLoading: false,
    predictionError: null,
  },
  priorAuth: {
    predictions: [],
    summary: {
      totalFlaggedMedications: 0,
      highProbabilityCount: 0,
      avgPaProbability: 0,
      avgApprovalDays: 0,
    },
    loading: false,
    error: null,
    checkResult: null,
    checkLoading: false,
    checkError: null,
  },
  meddTrajectories: {
    trajectories: [],
    summary: {
      totalPatientsOnOpioids: 0,
      patientsAbove90MME: 0,
      patientsApproaching90MME: 0,
      holyTrinityCount: 0,
      multiplePrescriberCount: 0,
      narcanComplianceRate: 0,
    },
    loading: false,
    error: null,
    simulation: {
      result: null,
      loading: false,
      error: null,
    },
  },
  status: 'idle',
  error: null,
};

const predictiveSlice = createSlice({
  name: 'predictive',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Summary
      .addCase(fetchDashboardSummary.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchDashboardSummary.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.summary = action.payload;
      })
      .addCase(fetchDashboardSummary.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      // Denials
      .addCase(fetchDenialRiskPredictions.fulfilled, (state, action) => {
        state.denialPredictions = action.payload;
      })
      // Adherence
      .addCase(fetchAdherenceRiskPredictions.fulfilled, (state, action) => {
        state.adherencePredictions = action.payload;
      })
      // Inventory
      .addCase(fetchInventoryForecasts.fulfilled, (state, action) => {
        state.inventoryForecasts = action.payload;
      })
      // PIP Exhaustion
      .addCase(fetchPipExhaustion.pending, (state) => {
        state.pipExhaustion.loading = true;
        state.pipExhaustion.error = null;
      })
      .addCase(fetchPipExhaustion.fulfilled, (state, action) => {
        state.pipExhaustion.loading = false;
        state.pipExhaustion.forecasts = action.payload.forecasts || [];
        state.pipExhaustion.summary = action.payload.summary || state.pipExhaustion.summary;
      })
      .addCase(fetchPipExhaustion.rejected, (state, action) => {
        state.pipExhaustion.loading = false;
        state.pipExhaustion.error = action.payload;
      })
      // Appeal Predictions
      .addCase(fetchAppealPredictions.pending, (state) => {
        state.appealPredictions.loading = true;
        state.appealPredictions.error = null;
      })
      .addCase(fetchAppealPredictions.fulfilled, (state, action) => {
        state.appealPredictions.loading = false;
        state.appealPredictions.predictions = action.payload.predictions || [];
        state.appealPredictions.summary = action.payload.summary || state.appealPredictions.summary;
      })
      .addCase(fetchAppealPredictions.rejected, (state, action) => {
        state.appealPredictions.loading = false;
        state.appealPredictions.error = action.payload;
      })
      // Denial Root Cause Analysis
      .addCase(fetchDenialRootCauseAnalysis.pending, (state) => {
        state.denialRootCauses.loading = true;
        state.denialRootCauses.error = null;
      })
      .addCase(fetchDenialRootCauseAnalysis.fulfilled, (state, action) => {
        state.denialRootCauses.loading = false;
        state.denialRootCauses.rootCauses = action.payload.rootCauses || [];
        state.denialRootCauses.summary = action.payload.summary || state.denialRootCauses.summary;
      })
      .addCase(fetchDenialRootCauseAnalysis.rejected, (state, action) => {
        state.denialRootCauses.loading = false;
        state.denialRootCauses.error = action.payload;
      })
      // Case Profitability Dashboard
      .addCase(fetchProfitabilityDashboard.pending, (state) => {
        state.caseProfitability.loading = true;
        state.caseProfitability.error = null;
      })
      .addCase(fetchProfitabilityDashboard.fulfilled, (state, action) => {
        state.caseProfitability.loading = false;
        state.caseProfitability.dashboard = action.payload || state.caseProfitability.dashboard;
      })
      .addCase(fetchProfitabilityDashboard.rejected, (state, action) => {
        state.caseProfitability.loading = false;
        state.caseProfitability.error = action.payload;
      })
      // Case Profitability Prediction
      .addCase(predictCaseProfitability.pending, (state) => {
        state.caseProfitability.predictionLoading = true;
        state.caseProfitability.predictionError = null;
      })
      .addCase(predictCaseProfitability.fulfilled, (state, action) => {
        state.caseProfitability.predictionLoading = false;
        state.caseProfitability.prediction = action.payload;
      })
      .addCase(predictCaseProfitability.rejected, (state, action) => {
        state.caseProfitability.predictionLoading = false;
        state.caseProfitability.predictionError = action.payload;
      })
      // Prior Auth Predictions
      .addCase(fetchPriorAuthPredictions.pending, (state) => {
        state.priorAuth.loading = true;
        state.priorAuth.error = null;
      })
      .addCase(fetchPriorAuthPredictions.fulfilled, (state, action) => {
        state.priorAuth.loading = false;
        state.priorAuth.predictions = action.payload.predictions || [];
        state.priorAuth.summary = action.payload.summary || state.priorAuth.summary;
      })
      .addCase(fetchPriorAuthPredictions.rejected, (state, action) => {
        state.priorAuth.loading = false;
        state.priorAuth.error = action.payload;
      })
      // Prior Auth Check
      .addCase(checkPriorAuthPrediction.pending, (state) => {
        state.priorAuth.checkLoading = true;
        state.priorAuth.checkError = null;
      })
      .addCase(checkPriorAuthPrediction.fulfilled, (state, action) => {
        state.priorAuth.checkLoading = false;
        state.priorAuth.checkResult = action.payload;
      })
      .addCase(checkPriorAuthPrediction.rejected, (state, action) => {
        state.priorAuth.checkLoading = false;
        state.priorAuth.checkError = action.payload;
      })
      // MEDD Trajectories
      .addCase(fetchMeddTrajectories.pending, (state) => {
        state.meddTrajectories.loading = true;
        state.meddTrajectories.error = null;
      })
      .addCase(fetchMeddTrajectories.fulfilled, (state, action) => {
        state.meddTrajectories.loading = false;
        state.meddTrajectories.trajectories = action.payload.trajectories || [];
        state.meddTrajectories.summary = action.payload.summary || state.meddTrajectories.summary;
      })
      .addCase(fetchMeddTrajectories.rejected, (state, action) => {
        state.meddTrajectories.loading = false;
        state.meddTrajectories.error = action.payload;
      })
      // MEDD Simulation
      .addCase(simulateMeddDispense.pending, (state) => {
        state.meddTrajectories.simulation.loading = true;
        state.meddTrajectories.simulation.error = null;
      })
      .addCase(simulateMeddDispense.fulfilled, (state, action) => {
        state.meddTrajectories.simulation.loading = false;
        state.meddTrajectories.simulation.result = action.payload;
      })
      .addCase(simulateMeddDispense.rejected, (state, action) => {
        state.meddTrajectories.simulation.loading = false;
        state.meddTrajectories.simulation.error = action.payload;
      });
  },
});

export default predictiveSlice.reducer;
